import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { Subject, takeUntil } from 'rxjs';

import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { CheckboxStateService } from "../../../core-module/services/state/checkbox-state.service";
import { FeatureDefaultsStateService } from "../../../core-module/services/state/feature-defaults-state.service";
import { InputErrorsStateService } from "../../../core-module/services/state/input-errors-state.service";
import { FeatureUtilsService } from "../../../core-module/services/state/feature-utils.service";
@Component({
  standalone: false,
  selector: 'app-dali102',
  templateUrl: './dali102.component.html',
  styleUrls: ['./dali102.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Dali102Component extends FieldType implements OnInit, OnDestroy {

  groups: boolean[] = new Array(16).fill(false);
  shortAddressValue: number | string;
  shortAddressError = false;
  defaultColor = true;
  isIncludedInConfiguration = false;
  isEdited: LegacyAny;
  pathVal: string;
  private ignoreParentUpdate = false;
  private hasSessionStorageData = false;
  private defaultShortAddress: number;
  private defaultGroups: string[] = [];
  private properties: LegacyAny;
  private shortAddressMin: number;
  private shortAddressMax: number;
  private shortAddressNoChangeValue: number;
  private destroy$ = new Subject<void>();

  constructor(
    private ref: ChangeDetectorRef, private appStateService: AppStateService,
    private featureDefaults: FeatureDefaultsStateService,
    private checkboxState: CheckboxStateService,
    private inputErrors: InputErrorsStateService,
    private utils: FeatureUtilsService
  ) {
    super();
  }

  ngOnInit(): void {
    // Initialize image path for info icon
    this.pathVal = this.utils.getImageUrl(window.location.hostname);
    
    // Protect initial load from parent checkbox updates
    this.ignoreParentUpdate = true;

    this.properties = (this.field as LegacyAny)?.properties;  // Get schema properties from field

    // Initialize from schema defaults - fully schema-driven
    this.defaultShortAddress = this.properties?.ShortAddress?.default;
    this.defaultGroups = this.properties?.Groups?.default ?? [];

    // Initialize validation ranges from schema (using oneOf pattern)
    // oneOf[0] contains the const value (no-change value, typically 255)
    // oneOf[1] contains the valid range (minimum and maximum)
    this.shortAddressNoChangeValue = (this.field as LegacyAny)?.oneOf?.[0]?.properties?.ShortAddress?.const ?? this.defaultShortAddress;
    this.shortAddressMin = (this.field as LegacyAny)?.oneOf?.[1]?.properties?.ShortAddress?.minimum ?? 0;
    this.shortAddressMax = (this.field as LegacyAny)?.oneOf?.[1]?.properties?.ShortAddress?.maximum ?? 63;


    const configData = this.appStateService.getConfigurationData();
    let sessionStorageValue = null;

    // Check UnSelectedFeatConfig first (for edited but unselected features)
    const unSelectedFeat = this.appStateService.getUnSelectedFeatConfig();
    let isConfigEdited = false;

    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex: number = 0;
      unSelectedFeat.forEach((element) => {
        if (element["featureName"] && element["featureName"].toString().toUpperCase() === "DALIADDRESSING102") {
          isConfigEdited = true;
          sessionStorageValue = element["featureParameters"][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appStateService.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    // If not in UnSelectedFeatConfig, check configData
    if (!isConfigEdited && configData) {
      try {
        const parsedConfig = configData;
        if (parsedConfig?.resp?.DaliAddressing102) {
          sessionStorageValue = parsedConfig.resp.DaliAddressing102;
          this.hasSessionStorageData = true;
        }
      } catch (e) {
        console.error('Error parsing configuration data from AppStateService', e);
      }
    }

    // Use centralized method to get feature data (prioritizes SaveSetData over ConfigurationData)
    if (!sessionStorageValue) {
      sessionStorageValue = this.appStateService.getFeatureData('DaliAddressing102') ||
        this.appStateService.getFeatureData('daliaddressing102');
    }


    // Subscribe to restore default trigger
    this.featureDefaults.getDali102AddressingDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === "valid") {
          this.defaultColor = true;
          this.resetToDefaults();
        }
      });

    let currentValue = sessionStorageValue || this.formControl.value || {};

    // Subscribe to parent "Include in configuration" checkbox changes
    this.checkboxState.getDali102AddressingParentCheckbox
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state !== null && !this.ignoreParentUpdate) {
          this.isIncludedInConfiguration = state.checked;

          // Handle unchecking: reset state
          if (!state.checked) {
            this.hasSessionStorageData = false;
            currentValue = {};
            this.updateFormControl();
            this.inputErrors.sendDali102AddressingInputError(false);
            this.ref.detectChanges();
            return;
          }

          // Handle checking: all flows
          if (this.hasSessionStorageData) {
            // Refresh flow: preserve current state, but still persist so
            // the file-builder can find the payload even without an edit.
            this.setConfigurationData(this.formControl.value, 'DaliAddressing102');
            return;
          }

          this.updateFormControl();
          // Persist current form values so save-feature-file has a payload
          // even if the user never edits the fields.
          this.setConfigurationData(this.formControl.value, 'DaliAddressing102');
          this.sendErrorStateToParent();
          this.ref.detectChanges();
        }
      });
    this.shortAddressValue = currentValue.ShortAddress ?? this.defaultShortAddress;

    const groupsArray = currentValue.Groups ?? this.defaultGroups;
    this.groups = new Array(16).fill(false);
    groupsArray.forEach((groupName: string) => {
      const match = groupName.match(/Group(\d+)/);
      if (match) {
        const index = parseInt(match[1], 10);
        if (index >= 0 && index <= 15) {
          this.groups[index] = true;
        }
      }
    });

    this.updateFormControl();

    // Enable parent checkbox if we have meaningful data
    const hasShortAddress = this.shortAddressValue !== this.defaultShortAddress;
    const hasGroups = this.groups.some(g => g === true);
    if (hasShortAddress || hasGroups) {
      this.checkboxState.enableDali102Checkbox();
      this.isIncludedInConfiguration = true;
    }

    // Revalidate all fields after loading from storage to restore error states
    this.revalidateAllFields();

    this.checkDefaults();

    this.isEdited = false;
    this.ignoreParentUpdate = false;
  }

  private revalidateAllFields(): void {
    // Revalidate short address if it has a value
    if (this.shortAddressValue !== undefined) {
      const numValue = typeof this.shortAddressValue === 'number'
        ? this.shortAddressValue
        : parseInt(String(this.shortAddressValue), 10);

      if (!isNaN(numValue)) {
        // Validate using schema-driven ranges
        if ((numValue >= this.shortAddressMin && numValue <= this.shortAddressMax) || numValue === this.shortAddressNoChangeValue) {
          this.shortAddressError = false;
        } else {
          this.shortAddressError = true;
        }
      }
    }

    // Send error state to parent to disable configure/save buttons if needed
    this.sendErrorStateToParent();
  }

  onGroupChange(): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    const anyGroupChecked = this.groups.some(group => group === true);
    const hasShortAddress = this.shortAddressValue !== this.defaultShortAddress;

    // Enable/disable parent checkbox based on meaningful data
    if (anyGroupChecked || hasShortAddress) {
      this.checkboxState.enableDali102Checkbox();
      this.isIncludedInConfiguration = true;
    } else {
      // Only disable if not currently checked
      if (!this.isIncludedInConfiguration) {
        this.checkboxState.disableDali102Checkbox();
      }
      // If checked, leave it checked (user explicitly wants defaults included)
    }

    this.ref.detectChanges();
    this.updateFormControl();
    this.checkDefaults();
    this.ignoreParentUpdate = false;
  }

  onShortAddressInput(value: string): void {
    this.ignoreParentUpdate = true;
    this.isEdited = true;
    this.ref.detectChanges();

    // Handle empty input - treat as no-change value
    if (value === '' || value === null || value === undefined) {
      this.shortAddressValue = this.shortAddressNoChangeValue;
      this.shortAddressError = false;
      this.sendErrorStateToParent();
      this.updateFormControl();
      this.checkDefaults();

      // Check if we should disable parent checkbox (no groups and default address)
      const hasGroups = this.groups.some(g => g === true);
      if (!hasGroups && this.shortAddressValue === this.defaultShortAddress) {
        // Only disable if not currently checked
        if (!this.isIncludedInConfiguration) {
          this.checkboxState.disableDali102Checkbox();
        }
        // If checked, leave it checked (user explicitly wants defaults included)
      }
      this.ignoreParentUpdate = false;
      return;
    }

    const numValue = parseInt(value, 10);
    this.shortAddressValue = numValue;

    // Validate using schema-driven ranges
    if ((numValue >= this.shortAddressMin && numValue <= this.shortAddressMax) || numValue === this.shortAddressNoChangeValue) {
      this.shortAddressError = false;
    } else {
      this.shortAddressError = true;
    }

    // Enable/disable parent checkbox based on meaningful data
    const hasGroups = this.groups.some(g => g === true);
    const hasShortAddress = numValue !== this.defaultShortAddress;

    if (hasShortAddress || hasGroups) {
      this.checkboxState.enableDali102Checkbox();
      this.isIncludedInConfiguration = true;
    } else {
      // Only disable if not currently checked
      if (!this.isIncludedInConfiguration) {
        this.checkboxState.disableDali102Checkbox();
      }
      // If checked, leave it checked (user explicitly wants defaults included)
    }

    this.sendErrorStateToParent();
    this.updateFormControl();
    this.checkDefaults();
    this.ignoreParentUpdate = false;
  }

  numberOnly(event: LegacyAny): boolean {
    const charCode = event.which ? event.which : event.keyCode;

    if (charCode < 48 || charCode > 57) {// Allow only numbers (0-9)
      event.preventDefault();
      return false;
    }
    return true;
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text');

    if (!pastedText) {
      return;
    }

    if (!/^\d+$/.test(pastedText)) {
      return;
    }

    this.onShortAddressInput(pastedText);
  }

  private updateFormControl(): void {
    if (!this.formControl) {
      return;
    }

    const value: LegacyAny = {};

    // Always include both ShortAddress and Groups
    value.ShortAddress = this.shortAddressValue;

    const groupsArray: string[] = [];
    this.groups.forEach((isEnabled, index) => {
      if (isEnabled) {
        groupsArray.push(`Group${index}`);
      }
    });
    value.Groups = groupsArray;

    this.formControl.setValue(value);

    // Save to sessionStorage after user edits
    if (this.isEdited) {
      this.setConfigurationData(value, 'DaliAddressing102');
    }
  }

  checkDefaults(): void {
    const isShortAddressDefault = this.shortAddressValue === this.defaultShortAddress;

    const currentGroups: string[] = [];
    this.groups.forEach((isEnabled, index) => {
      if (isEnabled) {
        currentGroups.push(`Group${index}`);
      }
    });

    const isGroupsDefault =
      currentGroups.length === this.defaultGroups.length &&
      currentGroups.every(group => this.defaultGroups.includes(group));

    this.defaultColor = isShortAddressDefault && isGroupsDefault;
    if (this.isIncludedInConfiguration || this.defaultColor) {
      this.featureDefaults.setDali102AddressingDefaultColor(this.defaultColor);
    }
  }

  resetToDefaults(): void {
    // Preserve checkbox state before resetting
    const wasIncludedInConfiguration = this.isIncludedInConfiguration;

    // Reset short address value to default
    this.shortAddressValue = this.defaultShortAddress;
    this.shortAddressError = false;

    // Uncheck all groups
    this.groups = new Array(16).fill(false);

    this.updateFormControl();
    this.defaultColor = true;
    this.featureDefaults.setDali102AddressingDefaultColor(this.defaultColor);

    // Clear error state when resetting to defaults
    this.inputErrors.sendDali102AddressingInputError(false);

    // Restore checkbox state - if it was checked, keep it checked
    if (wasIncludedInConfiguration) {
      this.isIncludedInConfiguration = true;
      this.checkboxState.enableDali102Checkbox();
    }

    this.setConfigurationData(this.formControl.value, 'DaliAddressing102');

    this.ref.detectChanges();
  }

  setConfigurationData(value, keyVal): void {
    if (value != null) {
      // Use centralized method to save feature data
      // This automatically saves to both ConfigurationData and SaveSetData
      const daliAddressing102Obj = Object.assign({}, value);
      this.appStateService.setFeatureData(keyVal, daliAddressing102Obj);
    }
  }

  private sendErrorStateToParent(): void {
    if (this.isIncludedInConfiguration) {
      this.inputErrors.sendDali102AddressingInputError(this.shortAddressError);
    } else {
      this.inputErrors.sendDali102AddressingInputError(false);
    }
  }

  openInfo(e): void {
    e.stopPropagation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
